import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import Orb from "@/components/Orb";

const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white grid md:grid-cols-2">
      {/* cinematic left */}
      <div className="relative hidden md:flex items-center justify-center overflow-hidden border-r border-white/[0.06]">
        <div className="holo-mesh opacity-70" aria-hidden />
        <div className="relative z-10 flex flex-col items-center gap-10 px-10">
          <Orb size={340} />
          <div className="text-center max-w-sm">
            <div className="label-mono mb-3">/ resume</div>
            <div className="font-display text-2xl tightest font-semibold leading-snug">
              Every lesson you&apos;ve ever recorded — <span className="chrome-text">right where you left them.</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-6 label-mono">v1 · study OS</div>
      </div>

      {/* form right */}
      <div className="relative flex items-center px-6 md:px-16 py-16">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex items-center justify-between mb-16">
            <Link to="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-white transition-colors text-[0.82rem]" data-testid="back-home">
              <ArrowLeft size={13} /> back
            </Link>
            <Link to="/" className="md:hidden"><Wordmark size="sm" /></Link>
          </div>

          <h1 className="font-display text-4xl tightest font-semibold mb-3 leading-none">Welcome back.</h1>
          <p className="text-neutral-500 text-[0.9rem] mb-10">Sign in to resume your study session.</p>

          <form onSubmit={onSubmit} className="space-y-8">
            <div>
              <div className="label-mono mb-2">Email</div>
              <input
                className="input-line"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@school.edu"
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Password</div>
              <input
                className="input-line"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
            </button>
          </form>

          <div className="mt-10 text-[0.85rem] text-neutral-500">
            New here?{" "}
            <Link to="/signup" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors" data-testid="link-signup">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
